import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Shirt } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable "issue a new uniform set" dialog.
 * Props:
 *   open, onOpenChange
 *   categories  - [{key, label, options[]}]
 *   employees   - [{user_id, name, employee_id, property_ids, ...}]  (used when employee not preselected)
 *   defaultUserId, defaultPropertyId  (optional)
 *   onCreated(issuance)
 */
export default function IssueUniformDialog({
  open, onOpenChange, categories = [], employees = null,
  defaultUserId = "", defaultPropertyId = "", onCreated,
}) {
  const today = () => new Date().toISOString().slice(0, 10);
  const [userId, setUserId] = useState(defaultUserId);
  const [categoryKey, setCategoryKey] = useState(categories[0]?.key || "");
  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [issuedAt, setIssuedAt] = useState(today());
  const [propertyId, setPropertyId] = useState(defaultPropertyId || "");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUserId(defaultUserId || "");
    setCategoryKey(categories[0]?.key || "");
    setSize("");
    setQuantity(1);
    setIssuedAt(today());
    setPropertyId(defaultPropertyId || "");
    setRemarks("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultUserId, defaultPropertyId]);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.key === categoryKey),
    [categories, categoryKey]
  );
  const selectedEmployee = useMemo(
    () => (employees || []).find((e) => (e.user_id || e.id) === userId),
    [employees, userId]
  );

  const submit = async () => {
    if (!userId) { toast.error("Pick an employee"); return; }
    if (!categoryKey) { toast.error("Pick a category"); return; }
    const qty = parseInt(quantity, 10);
    if (!qty || qty < 1) { toast.error("Quantity must be at least 1"); return; }
    setBusy(true);
    try {
      const payload = {
        user_id: userId,
        category_key: categoryKey,
        quantity: qty,
        size: size || null,
        issued_at: issuedAt || null,
        property_id: propertyId || null,
        remarks: remarks || null,
      };
      const { data } = await api.post("/uniform-issuances", payload);
      toast.success("Issuance recorded");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record issuance");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="issue-uniform-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shirt className="w-4 h-4" /> Issue uniform set</DialogTitle>
          <DialogDescription>Log what you actually handed to the employee. Use one entry per category — repeat for multiple categories.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {employees && (
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger data-testid="issue-uniform-employee"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.user_id || e.id} value={e.user_id || e.id}>
                      {e.name}{e.employee_id ? ` · ${e.employee_id}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={categoryKey} onValueChange={(v) => { setCategoryKey(v); setSize(""); }}>
                <SelectTrigger data-testid="issue-uniform-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Quantity (sets)</Label>
              <Input
                type="number" min={1} step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid="issue-uniform-quantity"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Size</Label>
              {(selectedCategory?.options || []).length > 0 ? (
                <Select value={size || "unset"} onValueChange={(v) => setSize(v === "unset" ? "" : v)}>
                  <SelectTrigger data-testid="issue-uniform-size"><SelectValue placeholder="(optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">— Not specified —</SelectItem>
                    {(selectedCategory.options || []).map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  placeholder="(optional)"
                  data-testid="issue-uniform-size"
                />
              )}
            </div>
            <div>
              <Label className="text-xs">Issued on</Label>
              <Input
                type="date"
                value={issuedAt}
                onChange={(e) => setIssuedAt(e.target.value)}
                data-testid="issue-uniform-date"
              />
            </div>
          </div>
          {(selectedEmployee?.property_ids || []).length > 1 && (
            <div>
              <Label className="text-xs">Property</Label>
              <Select value={propertyId || "auto"} onValueChange={(v) => setPropertyId(v === "auto" ? "" : v)}>
                <SelectTrigger data-testid="issue-uniform-property"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Use employee's primary</SelectItem>
                  {(selectedEmployee.property_ids || []).map((pid, i) => (
                    <SelectItem key={pid} value={pid}>{(selectedEmployee.property_names || [])[i] || pid}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Remarks (optional)</Label>
            <Input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. replacement after damage"
              data-testid="issue-uniform-remarks"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="issue-uniform-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Record issuance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
